import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Brand, ThemeToggle, StatusBadge, TeamAvatar, getFavorites } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Search, Heart, ArrowRight, Zap, QrCode, Trophy, NotebookText } from "lucide-react";

const COVER = "https://images.unsplash.com/photo-1666461941397-254b6ea059ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const RELEASE_NOTES = [
  {
    date: "19-10-2026",
    changes: [
      "Added scheduled dates and times to referee match-list cards, using the tournament timezone.",
      "Redesigned live referee scoring with two team panels, automatically saved +/− controls, and a per-match start/stop/reset stopwatch that survives refresh on the same device.",
      "Added purple break bands to host grid and list schedules, plus match start–end times and GMT offsets on time tiles.",
    ],
  },
  {
    date: "18-10-2026",
    changes: [
      "Added match start date/time pickers to both host schedule views and a first-match start prompt before auto-scheduling.",
      "Added browser-detected tournament timezones, timezone-aware scheduling and game time displays, and a timezone selector in host settings.",
      "Timezone selectors are editable during creation and in tournament settings, prefilling from the browser when no timezone has been saved.",
      "Removed tournament color customization and colored header accents, and removed Sport from tournament forms and pages.",
      "Fix: PNG logo uploads and image URLs work for teams and tournaments, and uploaded logos now appear in avatars and tournament listings.",
    ],
  },
  {
    date: "15-10-2026",
    changes: [
      "Fix: Release notes have a dedicated scroll area; click the panel to close it without interrupting scroll gestures.",
    ],
  },
  {
    date: "11-10-2026",
    changes: [
      "Added an app admin option to delete host accounts and their associated tournaments.",
      "Updated the Coachi app and browser logo.",
      "Fix: Release notes now scroll reliably and close when the notes panel is clicked.",
    ],
  },
  {
    date: "08-10-2026",
    changes: [
      "Added limited-use open team invitations that let participants create a team and continue directly as its captain.",
      "Added public/private tournament visibility controls; private tournaments are accessible only by direct URL and are hidden from search.",
      "Added a host overview warning when multiple teams in a tournament use the same name.",
      "Added a QR code and shareable team member URL to the captain page.",
      "Added tournament standings and finalized match results to captain and team member pages.",
      "Added separate captain permission switches for changing team names and team logos.",
      "Added reversible VIP status for hosts, allowing VIP hosts to activate tournaments without payment.",
      "Fix: Number fields can now be cleared naturally while entering a new value.",
      "Fix: Release notes are scrollable on all screen sizes and modal clicks no longer reach the page underneath.",
    ],
  },
  {
    date: "05-10-2026",
    changes: [
      "Updated the create tournament wizard flow to start with tournament type, continue with Settings, and finish with Size & format.",
      "Removed Sport from tournament creation and prevented selecting past tournament dates.",
      "Added editable field names in the host schedule overview, reflected across schedules and match views.",
      "Fix: Favorite button remains clickable when a tournament logo is shown on the viewer page.",
      "Fix: Removed the duplicate Setup menu item from host tournament management.",
      "Fix: Auto-scheduling now finishes all group-stage matches before scheduling quarterfinals, semifinals or finals.",
      "Fix: Schedule field headers and match blocks remain aligned, with horizontal scrolling when many fields are used.",
    ],
  },
  {
    date: "04-10-2026",
    changes: [
      "Added sponsor URL support and clickable sponsor links on viewer pages.",
      "Added Stripe payment settings for admins and one-time tournament payment gating before activation.",
      "Fixed tournament, sponsor, and team logo uploads for non-Emergent deployments.",
      "Improved sponsor management layout on mobile and removed the unused sponsor logo preview box.",
      "Added draft-state notices for viewers, team members, and captains before tournaments go live.",
      "Added a host option to allow team captains to rename their own team.",
      "Added a main website button on tournament viewer pages.",
      "Updated the browser tab title to Coachi | Tournament.",
    ],
  },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    const codes = getFavorites();
    if (codes.length) api.post("/public/favorites", { codes }).then((r) => setFavorites(r.data));
  }, []);

  const search = async (val) => {
    setQ(val);
    if (val.length < 1) { setResults([]); return; }
    const { data } = await api.get(`/public?q=${encodeURIComponent(val)}`);
    setResults(data);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-background/70 px-5 py-3 backdrop-blur-xl">
        <Brand />
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <ReleaseNotesDialog />
          {user ? (
            <Button onClick={() => nav(user.role === "admin" ? "/admin" : "/host")} data-testid="nav-dashboard">Dashboard</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => nav("/login")} data-testid="nav-login">Sign in</Button>
              <Button onClick={() => nav("/register")} data-testid="nav-register">Host a tournament</Button>
            </>
          )}
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={COVER} alt="" className="h-full w-full object-cover opacity-20 dark:opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 to-background" />
        </div>
        <div className="relative mx-auto max-w-5xl px-6 py-20 sm:py-28">
          <div className="text-label mb-4 text-primary">Amateur tournaments, made simple</div>
          <h1 className="font-heading text-4xl font-black leading-none tracking-tight sm:text-6xl lg:text-7xl">
            Run your tournament<br />in minutes.
          </h1>
          <p className="mt-6 max-w-xl text-lg text-muted-foreground">
            Create brackets, schedule matches, assign referees and let everyone follow along live — no complex league software.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" onClick={() => nav(user ? "/host/new" : "/register")} data-testid="cta-create">
              Create a tournament <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {favorites.length > 0 && (
            <div className="mt-10 max-w-lg">
              <div className="mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-accent" fill="currentColor" />
                <h2 className="font-heading text-xl font-bold">Your favorites</h2>
              </div>
              <div className="space-y-2">
                {favorites.map((t) => (
                  <Card key={t.public_code} className="card-lift flex cursor-pointer items-center justify-between p-3"
                    onClick={() => nav(`/t/${t.public_code}`)} data-testid={`favorite-${t.public_code}`}>
                    <div className="flex items-center gap-3">
                      <TeamAvatar team={{ name: t.name, logo_url: t.logo_url }} />
                      <div>
                        <div className={`font-semibold ${["completed","archived"].includes(t.status) ? "strike-through-name" : ""}`}>{t.name}</div>
                      </div>
                    </div>
                    <StatusBadge status={t.status} />
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 flex max-w-lg items-center gap-2 rounded-xl border bg-card p-2 shadow-sm">
            <Search className="ml-2 h-5 w-5 text-muted-foreground" />
            <Input
              value={q} onChange={(e) => search(e.target.value)}
              placeholder="Find a tournament to follow…"
              className="border-0 focus-visible:ring-0" data-testid="public-search-input"
            />
          </div>

          {results.length > 0 && (
            <div className="mt-3 max-w-lg space-y-2" data-testid="search-results">
              {results.map((t) => (
                <Card key={t.id} className="card-lift flex cursor-pointer items-center justify-between p-3"
                  onClick={() => nav(`/t/${t.public_code}`)} data-testid={`search-result-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <TeamAvatar team={{ name: t.name, logo_url: t.logo_url }} />
                    <div>
                      <div className={`font-semibold ${["completed","archived"].includes(t.status) ? "strike-through-name" : ""}`}>{t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.location || "—"}</div>
                    </div>
                  </div>
                  <StatusBadge status={t.status} />
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-16">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: Zap, t: "Auto structure", d: "Brackets, schedules & referee assignments generated instantly — fully editable." },
            { icon: QrCode, t: "QR access", d: "Captains, players, referees and fans reach the right screen with one scan." },
            { icon: Trophy, t: "Live results", d: "Scores and standings update live for everyone following the tournament." },
          ].map((f) => (
            <Card key={f.t} className="p-6">
              <f.icon className="h-6 w-6 text-primary" />
              <h3 className="mt-4 font-heading text-lg font-bold">{f.t}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.d}</p>
            </Card>
          ))}
        </div>
      </section>

    </div>
  );
}

function ReleaseNotesDialog() {
  const [open, setOpen] = useState(false);
  const gesture = useRef(null);
  const startGesture = (event) => {
    gesture.current = { x: event.clientX, y: event.clientY, scrolling: false };
  };
  const trackGesture = (event) => {
    const start = gesture.current;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 6) {
      start.scrolling = true;
    }
  };
  const closeOnClick = () => {
    if (!gesture.current?.scrolling) setOpen(false);
    gesture.current = null;
  };
  const stopClickThrough = (event) => {
    const original = event.detail?.originalEvent;
    if (original) {
      original.preventDefault();
      original.stopPropagation();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen} modal>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Release notes" data-testid="release-notes-button">
          <NotebookText className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="flex h-[80dvh] max-h-[calc(100dvh-2rem)] max-w-md flex-col overflow-hidden" onPointerDownOutside={stopClickThrough}
        onPointerDown={startGesture} onPointerMove={trackGesture}
        onPointerCancel={() => { if (gesture.current) gesture.current.scrolling = true; }}
        onClick={closeOnClick} data-testid="release-notes-dialog">
        <DialogHeader className="shrink-0 pr-6">
          <DialogTitle>Release notes</DialogTitle>
          <DialogDescription>Latest Coachi updates and changes.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 touch-pan-y space-y-4 overflow-y-auto overscroll-contain pr-2"
          style={{ WebkitOverflowScrolling: "touch" }} tabIndex={0} role="region" aria-label="Release notes updates"
          data-testid="release-notes-scroll-area">
          {RELEASE_NOTES.map((entry) => (
            <div key={entry.date}>
              <h3 className="font-heading text-sm font-bold">{entry.date}</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {entry.changes.map((change) => <li key={change}>{change}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
